/**
 * Carousel
 */
import { Component, registerComponent } from 'js/component.js';
import { isEditModeFooter } from 'js/helpers.js';
import { getBreakpointMediaQuery } from 'js/constants.js';
import Glide from '@glidejs/glide';
const componentName = 'carousel';
const carouselTotal = document.querySelectorAll(`.${componentName}__container`).length;
let carouselCount = 0;

class Carousel extends Component {

    /**
     * Component Setup
     */

    // Elements
    elements = {
        carouselContainer: this.element.querySelector(`.${componentName}__container`),
        glideSlides: this.element.querySelectorAll('.glide__slide'),
    };

    hasColumnControlWithTeasers = this.element.classList.contains('carousel--has-column-control-with-teasers');

    // Util & State
    // [LEGACY BREAKPOINT: max-width 48em -> getBreakpointMediaQuery('down', 'small')]
    matchMobile = getBreakpointMediaQuery('down', 'small');
    glideInstance = null;
    glideOptions = {
        type: null,
        rewind: false,
        startAt: 0,
        perView: null,
        autoplay: null,
        hoverpause: null,
        focusAt: null,
        autoheight: true,
        gap: 0,
    };

    // Check authored settings for carousel
    checkCarouselSettings = () => {

        // Check interval for delay
        const carouselDelay = this.elements.carouselContainer.getAttribute('data-cmp-delay');
        this.glideOptions.autoplay = carouselDelay ? parseInt(carouselDelay, 10) : 0;

        // Check the status of autoplay
        const isAutoPlayDisabled = this.elements.carouselContainer.getAttribute('data-cmp-autoplay') === null;
        this.glideOptions.autoplay = isAutoPlayDisabled ? false : this.glideOptions.autoplay;

        // Check to see if pause on hover is disabled
        const isAutoPauseEnabled = this.elements.carouselContainer.getAttribute('data-cmp-autopause-disabled') === null;
        this.glideOptions.hoverpause = isAutoPauseEnabled;

        // Get Carousel Type
        const carouselType = this.elements.carouselContainer.getAttribute('data-cmp-type');
        this.glideOptions.type = carouselType;

        // Get How Many Slides Per View
        const perView = this.elements.carouselContainer.getAttribute('data-cmp-per-view');
        this.glideOptions.perView = this.matchMobile.matches ? 1 : perView;

        // Get Glide Focus
        const focusAt = this.elements.carouselContainer.getAttribute('data-cmp-focus-at');
        this.glideOptions.focusAt = focusAt;
    };

    /**
     * Initialize Carousel Component Glide Carousel
     */
    initializeGlideCarousel = () => {

        // Set Up Glide Options + Instance
        const totalSlides = this.elements.glideSlides.length;
        if (!totalSlides) return false;

        if (!this.glideInstance) {
            this.glideInstance = new Glide(`#glide-${this.elements.carouselContainer.getAttribute('id')}`, this.glideOptions);

            if (this.hasColumnControlWithTeasers) {
                this.glideInstance.on('build.after', () => {
                    this.resizeCarouselHeight();
                });

                this.glideInstance.on('run.after', () => {
                    this.resizeCarouselHeight();
                });
            }

            // Remove clones from tab navigation
            const cloneClassName = this.glideInstance?.settings?.classes?.slide?.clone;
            const removeClonesFromTabNav = () => {
                if (cloneClassName) {
                    const cloneLinks = this.element.querySelectorAll(`.${cloneClassName} a`);
                    cloneLinks.forEach((link) => {
                        link.setAttribute('tabindex', '-1');
                    });
                }
            };
            this.glideInstance.on('mount.after', removeClonesFromTabNav);
            this.glideInstance.on('run.after', removeClonesFromTabNav);

            // Mount Glide
            this.glideInstance.mount();

            carouselCount += 1;
            if (carouselCount === carouselTotal) {
                console.debug('Dispatching init-video-players');
                window.dispatchEvent(new CustomEvent('init-video-players'));
            }
        } else {
            // Update Glide
            this.glideInstance.update(this.glideOptions);
        }

        // inkoo testing start
        if (typeof window.myGlideInstances === 'undefined') {
            window.myGlideInstances = [];
        }
        window.myGlideInstances.push(this.glideInstance);
        // inkoo testing stop

        window.dispatchEvent(new Event('resize'));

        return true;
    };

    resizeCarouselHeight() {
        const activeSlide = this.element.querySelector('.glide__slide--active');
        const activeSlideHeight = activeSlide ? activeSlide.offsetHeight : 0;

        const glideTrack = this.element.querySelector('.glide__track');
        const glideTrackHeight = glideTrack ? glideTrack.offsetHeight : 0;

        if (activeSlideHeight !== glideTrackHeight) {
            glideTrack.style.height = `${activeSlideHeight}px`;
        }
    }

    /**
     * On Window Resize
     */
    onWindowResize = () => {
        // glideInstance.update does not work for the perView property
        this.checkCarouselSettings();
        this.glideInstance.destroy();
        this.glideInstance = null;
        const slideClones = this.element.querySelectorAll('.glide__slide--clone');
        if (slideClones.length > 0) {
            slideClones.forEach((slideClone) => {
                slideClone.remove();
            });
        }
        this.initializeGlideCarousel();
    };

    /**
     * Add Event Listeners
     */
    addEventListeners = () => {
        this.matchMobile.addEventListener('change', this.onWindowResize);
        window.addEventListener('resize-carousel', this.onWindowResize);
    };

    /**
     * Initialize Component
     */
    init() {

        // Fire Init Funcs
        if (!isEditModeFooter() && this.elements.carouselContainer) {
            this.checkCarouselSettings();
            this.addEventListeners();
            // Only defer initialization when this carousel contains content that
            // the related-content loader fetches and swaps in asynchronously
            // (dynamic content tiles or a related-content grid). Static content
            // tiles are already in the DOM, so the carousel can initialize
            // immediately -- deferring on those left carousels stuck forever,
            // because "init-carousel" is fired only by the dynamic loader, which
            // never runs when there is nothing dynamic to fetch.
            const hasDynamicContent = this.elements.carouselContainer.querySelector(
                '.content-tile__container[data-type="dynamic"], .related-content__container',
            ) != null;

            if (hasDynamicContent && !window.piraContentTilesLoaded) {
                // Wait for the dynamic content to load before initializing Glide.
                window.addEventListener('init-carousel', this.initializeGlideCarousel, { once: true });
                // Safety net: initialize anyway if the load signal never arrives
                // (e.g. an empty or failed relatedContent fetch) so the carousel
                // can never be left permanently uninitialized. initializeGlideCarousel
                // is idempotent (guards on this.glideInstance), so whichever of the
                // event or this timeout fires second is a harmless no-op.
                setTimeout(this.initializeGlideCarousel, 5000);
                return;
            }

            this.initializeGlideCarousel();
        }
    }
}

registerComponent(`.${componentName}`, Carousel);

export default Carousel;
